import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Link } from "react-router-dom";
import { PageHeader, Badge } from "../../components/ui-extras";
import { DownloadSimple } from "@phosphor-icons/react";
import { backendDownloadUrl, downloadFromBackend } from "../../lib/download";

function invoicePdfHref(invoiceId) {
  return backendDownloadUrl(`/invoices/${invoiceId}/pdf`);
}

const downloadInvoicePdf = async (invoiceId) => {
  try {
    await downloadFromBackend(`/invoices/${invoiceId}/pdf`, {});
  } catch (_) {}
};

const NETWORK_LABEL = { esso: "Esso", pipeline: "Pipeline", petro_pass: "Petro-Pass" };

// C7 UI FIX B (2026-02) — derive canonical authorized networks from the
// authenticated customer's profile using the same union-of-representations
// convention already relied on by CustomerPriceLists and CustomerTransactions:
//   * `price_report_networks`: legacy Esso/Pipeline authorization
//   * `authorized_lanes`: newer per-lane {network,country} for Petro-Pass
// Compound lane IDs (e.g. `petro_pass_ca`) are collapsed to their canonical
// portal network so the UI never surfaces `esso_us`, `ta_petro`, `Both`, etc.
function deriveAuthorizedInvoiceNetworks(customerProfile) {
  const out = new Set();
  const priceReport = (customerProfile && customerProfile.price_report_networks) || [];
  const lanes = (customerProfile && customerProfile.authorized_lanes) || [];
  const _canonical = (raw) => {
    const s = String(raw || "").toLowerCase().trim();
    if (s === "esso" || s.startsWith("esso_")) return "esso";
    if (s === "ta_petro") return "esso"; // preserve Esso-USA read-layer duality — never expose ta_petro
    if (s === "pipeline" || s.startsWith("pipeline_")) return "pipeline";
    if (s === "petro_pass" || s.startsWith("petro_pass_")) return "petro_pass";
    return "";
  };
  priceReport.forEach((n) => {
    const c = _canonical(n);
    if (c) out.add(c);
  });
  lanes.forEach((l) => {
    // authorized_lanes may be objects `{network, country}` OR compound strings
    const raw = l && typeof l === "object" ? l.network : l;
    const c = _canonical(raw);
    if (c) out.add(c);
  });
  return ["esso", "pipeline", "petro_pass"].filter((n) => out.has(n));
}

export default function CustomerInvoices() {
  const { user } = useAuth();
  const customerId = user && user.customer_id;
  const [rows, setRows] = useState([]);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [network, setNetwork] = useState("");
  const [issuedFrom, setIssuedFrom] = useState("");
  const [issuedTo, setIssuedTo] = useState("");

  // Fetch profile from the same endpoint every other Customer Portal page uses.
  // Previous C7 R1 hit a non-existent profile route and swallowed the 404 —
  // that left `authorized networks` permanently empty for every customer.
  useEffect(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}`)
      .then((res) => setCustomerProfile((res && res.data) || {}))
      .catch(() => setCustomerProfile({}));
  }, [customerId]);

  const authorizedNetworks = useMemo(
    () => deriveAuthorizedInvoiceNetworks(customerProfile),
    [customerProfile],
  );

  // Stale-selection reconciler: if the currently-selected network is no longer
  // authorized (profile refresh, session switch), reset to "All networks".
  useEffect(() => {
    if (network && !authorizedNetworks.includes(network)) setNetwork("");
  }, [authorizedNetworks, network]);

  const fetchInvoices = () => {
    const params = {};
    if (network) params.network = network;
    if (issuedFrom) params.issued_from = issuedFrom;
    if (issuedTo) params.issued_to = issuedTo;
    api.get("/invoices", { params }).then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  };

  useEffect(() => {
    if (issuedFrom && issuedTo && issuedFrom > issuedTo) return; // block invalid range
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network, issuedFrom, issuedTo]);

  const clearFilters = () => { setNetwork(""); setIssuedFrom(""); setIssuedTo(""); };
  const rangeInvalid = issuedFrom && issuedTo && issuedFrom > issuedTo;

  return (
    <>
      <PageHeader title="Your invoices" subtitle="PORTAL / INVOICES" testId="portal-invoices-header" />
      <div className="gf-card">
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="portal-invoices-filters">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Network</label>
            <select
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              className="gf-input"
              data-testid="portal-invoices-network"
            >
              <option value="">All networks</option>
              {authorizedNetworks.map((n) => (
                <option key={n} value={n} data-testid={`portal-invoices-network-opt-${n}`}>{NETWORK_LABEL[n]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Issued Date From</label>
            <input
              type="date"
              value={issuedFrom}
              onChange={(e) => setIssuedFrom(e.target.value)}
              className="gf-input"
              data-testid="portal-invoices-issued-from"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Issued Date To</label>
            <input
              type="date"
              value={issuedTo}
              onChange={(e) => setIssuedTo(e.target.value)}
              className="gf-input"
              data-testid="portal-invoices-issued-to"
            />
          </div>
          <button
            className="gf-btn gf-btn-ghost"
            onClick={clearFilters}
            data-testid="portal-invoices-clear-filters"
          >Clear</button>
          {rangeInvalid && (
            <span className="text-xs text-red-600" data-testid="portal-invoices-range-error">Issued Date From must be on or before Issued Date To</span>
          )}
        </div>
        <table className="gf-table">
          <thead><tr><th>#</th><th>ISSUED</th><th>DUE</th><th>PERIOD</th><th className="text-right">TOTAL</th><th className="text-right">PAID</th><th>STATUS</th><th></th></tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.invoice_id}>
                <td><Link className="font-mono text-xs font-semibold hover:underline" to={`/portal/invoices/${i.invoice_id}`} data-testid={`portal-invoice-${i.invoice_id}`}>{i.invoice_number}</Link></td>
                <td className="font-mono text-xs">{i.issue_date}</td>
                <td className="font-mono text-xs">{i.due_date}</td>
                <td className="font-mono text-xs">{i.period_start} → {i.period_end}</td>
                <td className="gf-num font-semibold">${i.total?.toFixed(2)}</td>
                <td className="gf-num">${(i.paid_amount || 0).toFixed(2)}</td>
                <td><Badge variant={i.status === "paid" ? "success" : i.status === "overdue" ? "error" : "info"}>{i.status}</Badge></td>
                <td><button className="gf-btn gf-btn-ghost" onClick={() => downloadInvoicePdf(i.invoice_id)} data-testid={`portal-invoice-download-${i.invoice_id}`}><DownloadSimple size={14}/></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-400 py-8" data-testid="portal-invoices-empty">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
